/**
 * The 2026 redesign rollout: which screens are ready, and who can see them.
 *
 * THE PROBLEM THIS SOLVES. The re-skin touches every organiser screen. Landing
 * it in one PR means a month-long branch that conflicts with everything;
 * landing it screen by screen on main means organisers see a half-migrated
 * dashboard. Neither is acceptable.
 *
 * So there are TWO INDEPENDENT GATES, and a screen renders new only when both
 * pass:
 *
 *   1. WHO   - `REDESIGN_ALLOWLIST`, Clerk user ids. Anyone not on it always
 *              gets the current UI, on every screen, no exceptions.
 *   2. WHICH - the registry below. A screen marked `building` renders the
 *              current UI even for an allowlisted reviewer.
 *
 * Why both. The allowlist alone would show the reviewer half-built screens with
 * no way to say "this one is not ready yet". The registry alone would put
 * every finished screen in front of organisers the moment it merged. Together,
 * the reviewer walks a dashboard that is part new and part old, every screen
 * works, and the new ones can be tested one at a time against real data.
 *
 * THE FINAL FLIP is one environment variable. When every screen is `ready` and
 * signed off, `REDESIGN_LIVE=true` gives the whole thing to everyone, and the
 * allowlist stops mattering. No code change, so it is also the rollback: unset
 * it and every organiser is back on the current UI within a deploy.
 *
 * This module is pure so it can be tested without Clerk, cookies or a request.
 * The server-side entry point is lib/redesign/server.ts.
 */

/**
 * Every organiser-side screen the redesign covers.
 *
 * `ready` means: built, wired to the real route, and function-parity audited
 * against the live component (see docs/plans/redesign-function-parity.md). A
 * preview at /design-tokens is NOT enough: those run on invented data, and
 * ready means the real route renders the new screen with real data.
 *
 * It does NOT mean signed off. Sign-off is what gates REDESIGN_LIVE. Requiring
 * it here would deadlock: a reviewer cannot sign off a screen they cannot see,
 * and they cannot see it until it is ready. So ready means "a reviewer on the
 * allowlist should now look at this", and no organiser is affected either way.
 */
export type RedesignStatus = 'ready' | 'building';

export type RedesignScreen =
  // Event level, the thirteen tabs plus the two new destinations
  | 'event.setup'
  | 'event.branding'
  | 'event.registrants'
  | 'event.volunteers'
  | 'event.requests'
  | 'event.groups'
  | 'event.ancillaries'
  | 'event.schedule'
  | 'event.checkin'
  | 'event.scoring'
  | 'event.analytics'
  | 'event.comms'
  | 'event.fundraising'
  | 'event.tickets'
  | 'event.spectators'
  | 'event.photos'
  | 'event.overview'
  /** Redesign only: consent and safety records (health answers, signatures,
   *  guardian co-signatures, photo consent, emergency contacts) on one page. */
  | 'event.compliance'
  | 'event.vatReport'
  // Organiser level
  | 'org.home'
  | 'org.events'
  | 'org.finance'
  | 'org.volunteers'
  | 'org.registrants'
  | 'org.campaigns'
  | 'org.contacts'
  | 'org.settings'
  | 'org.billing'
  | 'org.account'
  | 'org.onboarding'
  // Create an event
  | 'create.wizard'
  /**
   * The chrome itself: the rail, the top bar, the organisation switcher.
   *
   * NOT a screen, and it cannot be gated like one. Turning it on puts every
   * tab body inside the new frame, whatever that tab's own flag says. Every
   * tab now has a v8 body, but a tab flipped back to `building` would show its
   * old body inside this frame.
   */
  | 'shell';

/**
 * The registry. Everything starts as `building`.
 *
 * Flipping one of these to `ready` is the deliberate act that puts a screen in
 * front of the reviewer. It should happen in the PR that finishes the screen,
 * never in a batch afterwards, so the flip and the screen are reviewed
 * together.
 */
export const REDESIGN_SCREENS: Record<RedesignScreen, RedesignStatus> = {
  // First slice 7 October 2026: the handoff's grouped rows (Still to do,
  // Done, Optional) around every existing section, each in the new colours.
  // Parity is total: the sections are the same components. Their insides are
  // converted to the v8 controls slice by slice.
  'event.setup': 'ready',
  // 9 October 2026: every other event screen wired on v8 (Branding, Schedule,
  // Check-in, Scoring, Analytics, Groups, Comms, VAT report; Volunteers and
  // Fundraising as their coming-soon pages). Each keeps the old look for
  // organisers not on the allowlist.
  'event.branding': 'ready',
  // Wired 22 September 2026. The redesign swaps the toolbar and the table;
  // the detail sheet is the same component both lists render, so nothing in
  // it can have been lost.
  'event.registrants': 'ready',
  'event.volunteers': 'ready',
  // Wired 23 September 2026. The parity audit against the live component
  // turned up the completed-status bug, which is fixed on both screens.
  'event.requests': 'ready',
  'event.groups': 'ready',
  // Wired 28 September 2026 (#1006): the list, figures, filters, the category
  // picker and the product window with sizes, stock and checkout questions.
  // Orders is on v8 too (FvdAncillaryOrdersList and its order window). Photo
  // pre-purchases moved to Photos (#994), which is also ready below, so the
  // two reach organisers together.
  'event.ancillaries': 'ready',
  'event.schedule': 'ready',
  'event.checkin': 'ready',
  'event.scoring': 'ready',
  'event.analytics': 'ready',
  'event.comms': 'ready',
  'event.fundraising': 'ready',
  // Wired 5 October 2026: the list on the shared table and the ticket window,
  // every field of the Setup form, saved through the same payload builder.
  // Setup keeps a pointer to it. Early bird and spectator tickets follow.
  'event.tickets': 'ready',
  // New on the redesign, 7 October 2026: one row per person with a spectator
  // ticket, from spectator_tickets. Only in the rail once the event sells one.
  'event.spectators': 'ready',
  // Wired 26 September 2026, tab by tab, and every tab (Photos, Sharing,
  // Sales, Privacy) is now on the v8 design. Only REDESIGN_ALLOWLIST sees it.
  'event.photos': 'ready',
  // Wired 26 September 2026: countdown, entries, divisions and activity, with
  // the event header moving here from every other tab and the event name
  // moving into the rail. The task list ("Before event day") is #782.
  'event.overview': 'ready',
  // New on the redesign, 12 October 2026: every consent and safety record in
  // one place. Health responses moved here from Setup; Setup keeps the
  // questionnaire, the waiver documents and the age rules.
  'event.compliance': 'ready',
  'event.vatReport': 'ready',
  'org.home': 'ready',
  'org.events': 'ready',
  'org.finance': 'ready',
  'org.volunteers': 'ready',
  // The organisation-level "All registrants" screen is the Contacts view
  // (org.contacts): one row per person across every event, each opening their
  // entries on the event's own Registrants tab. There is no separate route to
  // build, so this follows org.contacts.
  'org.registrants': 'ready',
  'org.campaigns': 'ready',
  'org.contacts': 'ready',
  'org.settings': 'ready',
  'org.billing': 'ready',
  'org.account': 'ready',
  'org.onboarding': 'ready',
  // Wired 4 October 2026: the shell, Template, Basics, Format and Tickets on
  // the format registry, then Page (brand, look and address beside the real
  // page preview) and Review (publish checks and go live), all on v8.
  // Anyone the gate turns away keeps the previous wizard (LegacyWizard).
  'create.wizard': 'ready',
  // Wired 23 September 2026. Every event tab and organisation screen in this
  // registry now has its v8 body, so the frame no longer wraps any old-look screen for a
  // reviewer on the allowlist.
  'shell': 'ready',
};

/** Parse `REDESIGN_ALLOWLIST`: comma or whitespace separated Clerk user ids. */
export function parseAllowlist(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export type RedesignEnv = {
  /** REDESIGN_LIVE. Anything other than the exact string 'true' is off. */
  live?: string | null;
  /** REDESIGN_ALLOWLIST. */
  allowlist?: string | null;
};

/**
 * Should this user see the redesigned version of this screen?
 *
 * The order matters and each step is a deliberate refusal:
 *
 *   - `REDESIGN_LIVE=true` wins outright. That is the launch switch, and once
 *     it is on the allowlist and the registry are both irrelevant: a screen
 *     still marked `building` at that point is a mistake we want visible, not
 *     one we want silently hidden from half the users.
 *   - Otherwise a missing user id is a no. An anonymous or unresolved request
 *     never gets unreleased UI.
 *   - Otherwise the user must be on the allowlist.
 *   - Otherwise the screen must be `ready`.
 *
 * Anything unrecognised is a no, because the failure mode of a no is "an
 * organiser sees the product they already use".
 */
export function shouldShowRedesign(
  screen: RedesignScreen,
  userId: string | null | undefined,
  env: RedesignEnv,
): boolean {
  if (env.live === 'true') return true;
  if (!userId) return false;
  if (!parseAllowlist(env.allowlist).includes(userId)) return false;
  return REDESIGN_SCREENS[screen] === 'ready';
}

/** Progress, for the rollout status page and for the PR body. */
export function redesignProgress(): { ready: number; total: number; remaining: RedesignScreen[] } {
  const entries = Object.entries(REDESIGN_SCREENS) as Array<[RedesignScreen, RedesignStatus]>;
  const remaining = entries.filter(([, s]) => s !== 'ready').map(([k]) => k);
  return { ready: entries.length - remaining.length, total: entries.length, remaining };
}

/**
 * Whether a dashboard path is inside a single event, where the event page draws
 * its own rail and the shell stands aside. /dashboard/events/new/** is not:
 * creating an event happens in the organisation, and matching "new" as an id
 * left the create screens with no rail at all.
 */
export function isEventDetailPath(pathname: string | null | undefined): boolean {
  // The VAT report is a standalone page with no event rail of its own, so it
  // keeps the organisation rail rather than rendering with none.
  return /^\/dashboard\/events\/(?!new(?:\/|$))[^/]+(?:\/(?!vat-report(?:\/|$)).*)?$/.test(pathname ?? '');
}
